import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import InvoiceDetailView from './InvoiceDetailView';
import { 
  Search, 
  Download, 
  Eye, 
  CheckCircle, 
  Clock, 
  XCircle, 
  FileText, 
  Printer,
  Calendar,
  DollarSign,
  Building2,
  FileX,
  RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';

const monthlyCreditNotes = {
  '2025-10': [
    {
      number: 'CN-2024-0045',
      date: '2025-10-23',
      dueDate: '2025-11-23',
      vendor: 'Retail Partners',
      relatedInvoice: 'INV-2024-1220',
      serviceProvider: 'ServicePro UAE',
      items: [
        { description: 'Product Return - Damaged Goods', quantity: 3, unitPrice: 396.83, taxRate: 5, amount: 1250 }
      ],
      subtotal: 1190.48,
      tax: 59.52,
      total: 1250,
      status: 'pending',
      ftaRef: 'Pending',
      erpSource: 'SAP',
      reason: 'Damaged goods returned by customer'
    },
    {
      number: 'CN-2024-0044',
      date: '2025-10-20',
      dueDate: '2025-11-20',
      vendor: 'Tech Solutions Inc',
      relatedInvoice: 'INV-2024-1210',
      serviceProvider: 'ServicePro UAE',
      items: [
        { description: 'Service Credit - Early Termination', quantity: 1, unitPrice: 1904.76, taxRate: 5, amount: 2000 }
      ],
      subtotal: 1904.76,
      tax: 95.24,
      total: 2000,
      status: 'processed',
      ftaRef: 'FTA-2024-CN-044',
      erpSource: 'SAP',
      reason: 'Contract terminated early - credit issued'
    },
    {
      number: 'CN-2024-0043',
      date: '2025-10-18',
      dueDate: '2025-11-18',
      vendor: 'Global Enterprises',
      relatedInvoice: 'INV-2024-1205',
      serviceProvider: 'ServicePro UAE',
      items: [
        { description: 'Billing Error Correction', quantity: 1, unitPrice: 714.29, taxRate: 5, amount: 750 }
      ],
      subtotal: 714.29,
      tax: 35.71,
      total: 750,
      status: 'processed',
      ftaRef: 'FTA-2024-CN-043',
      erpSource: 'SAP',
      reason: 'Billing error correction'
    }
  ],
  '2025-09': [
    {
      number: 'CN-2024-0038',
      date: '2025-09-25',
      dueDate: '2025-10-25',
      vendor: 'Office Supplies Co',
      relatedInvoice: 'INV-2024-1150',
      serviceProvider: 'ServicePro UAE',
      items: [
        { description: 'Discount Applied - Bulk Order', quantity: 1, unitPrice: 476.19, taxRate: 5, amount: 500 }
      ],
      subtotal: 476.19,
      tax: 23.81,
      total: 500,
      status: 'processed',
      ftaRef: 'FTA-2024-CN-038',
      erpSource: 'SAP',
      reason: 'Volume discount applied'
    }
  ]
};

export default function SalesCreditNotes() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('2025-10');
  const [selectedNote, setSelectedNote] = useState<any>(null);
  const [isDetailView, setIsDetailView] = useState(false);

  const currentNotes = monthlyCreditNotes[selectedMonth as keyof typeof monthlyCreditNotes] || [];

  const filteredNotes = currentNotes.filter(note =>
    note.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.vendor.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.relatedInvoice.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'processed':
        return (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100">
            <CheckCircle className="w-3 h-3 mr-1" />
            Processed
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      case 'failed':
        return (
          <Badge className="bg-rose-100 text-rose-700 border-rose-200 hover:bg-rose-100">
            <XCircle className="w-3 h-3 mr-1" />
            Failed
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const handleViewNote = (note: any) => {
    setSelectedNote(note);
    setIsDetailView(true);
  };

  const handleBackToList = () => {
    setIsDetailView(false);
    setSelectedNote(null);
  };

  const handleDownloadPDF = () => {
    toast.success('PDF download started');
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    toast.success('Sales credit notes exported successfully');
  };

  const formatMonthDisplay = (month: string) => {
    const date = new Date(month + '-01');
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Show detail view if a note is selected
  if (isDetailView && selectedNote) {
    return (
      <InvoiceDetailView 
        invoice={selectedNote}
        onBack={handleBackToList}
        type="sales"
      />
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6 bg-gradient-to-br from-slate-50 to-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg blur-md opacity-50"></div>
            <div className="relative p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg premium-shadow">
              <RotateCcw className="w-6 h-6 text-white" />
            </div>
          </div>
          <div>
            <h1 className="text-3xl text-gray-900">Sales Credit Notes</h1>
            <p className="text-gray-600 mt-1">Manage credit notes issued for sales invoices</p>
          </div>
        </div>
        <Button className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 shadow-lg" onClick={handleExport}>
          <Download className="w-4 h-4 mr-2" />
          Export Data
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-purple-500 shadow-sm hover:shadow-md transition-shadow hover-lift">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Total Credit Notes</p>
                <p className="text-3xl text-gray-900">{currentNotes.length}</p>
                <p className="text-xs text-gray-500 mt-2">{formatMonthDisplay(selectedMonth)}</p>
              </div>
              <div className="p-3 bg-purple-50 rounded-lg">
                <RotateCcw className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-pink-500 shadow-sm hover:shadow-md transition-shadow hover-lift">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Total Amount</p>
                <p className="text-3xl text-gray-900">
                  {currentNotes.reduce((sum, note) => sum + note.total, 0).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-2">AED</p>
              </div>
              <div className="p-3 bg-pink-50 rounded-lg">
                <DollarSign className="w-5 h-5 text-pink-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500 shadow-sm hover:shadow-md transition-shadow hover-lift">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Processed</p>
                <p className="text-3xl text-green-600">
                  {currentNotes.filter(note => note.status === 'processed').length}
                </p>
                <p className="text-xs text-green-600 mt-2">Successfully sent to FTA</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500 shadow-sm hover:shadow-md transition-shadow hover-lift">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Pending</p>
                <p className="text-3xl text-amber-600">
                  {currentNotes.filter(note => note.status === 'pending').length}
                </p>
                <p className="text-xs text-amber-600 mt-2">Awaiting processing</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="shadow-sm premium-shadow">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex-1 relative w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by credit note number, vendor, or related invoice..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11"
              />
            </div>
            <div className="w-full md:w-64">
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="h-11">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span className="flex-1 text-left">{formatMonthDisplay(selectedMonth)}</span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2025-10">October 2025</SelectItem>
                  <SelectItem value="2025-09">September 2025</SelectItem>
                  <SelectItem value="2025-08">August 2025</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Credit Notes Table */}
      <Card className="shadow-sm premium-shadow hover-lift">
        <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-pink-50">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-purple-600" />
                Sales Credit Notes - {formatMonthDisplay(selectedMonth)}
              </CardTitle>
              <CardDescription className="mt-1">
                Showing {filteredNotes.length} of {currentNotes.length} credit notes
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 hover:bg-gray-50">
                  <TableHead>Credit Note #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Related Invoice</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Amount (AED)</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredNotes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12">
                      <div className="flex flex-col items-center gap-2 text-gray-500">
                        <FileX className="w-12 h-12 text-gray-300" />
                        <p>No credit notes found</p>
                        <p className="text-sm">Try adjusting your search or filters</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredNotes.map((note) => (
                    <TableRow key={note.number} className="hover:bg-gray-50/50">
                      <TableCell className="text-purple-600">{note.number}</TableCell>
                      <TableCell className="text-gray-700">{note.date}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          {note.relatedInvoice}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-900">{note.vendor}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600 max-w-xs truncate">{note.reason}</TableCell>
                      <TableCell className="text-right text-gray-900">{note.total.toLocaleString()}</TableCell>
                      <TableCell>{getStatusBadge(note.status)}</TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 h-8 w-8 p-0"
                          onClick={() => handleViewNote(note)}
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
